// ФТУЕ.1: Pre-FTUE флоу — 5 экранов перед основным туториалом.
//   step 0 — Splash «4 корпы. Одна ставка.»
//   step 1 — Выбор языка RU/EN
//   step 2 — Позывной (ник)
//   step 3 — Сигнал (реф-код пригласившего)
//   step 4 — «Пройти обучение?» Да/Нет
//
// Гейт: показывается если player.ftueStatus ∈ {pending, in_progress} AND
// step < 10 (10..19 — туториал-бой, отдельный экран).
//
// Сервер: каждый шаг сохраняется через setFtueState('advance', step).
// Финал: 'Да' → 'complete' (бонус выдаётся отдельно на ФТУЕ.5);
//        'Нет' → 'skip' (базовый стартовый бандл уже выдан при auth).

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../../store/useGameStore';
import { useTelegram } from '../../telegram/useTelegram';
import {
  setFtueState, updateProfile, setReferrer
} from '../../api/client';
import { useLocale } from '../../i18n/useLocale';
import { useTr } from '../../i18n/useTr';
import s from './PreFTUE.module.css';

const TOTAL_STEPS = 5;

export function PreFTUE() {
  // ВСЕ хуки — ДО любого early return (React Rules of Hooks).
  const navigate = useNavigate();
  const ftueStatus = useGameStore(g => g.ftueStatus);
  const ftueStep   = useGameStore(g => g.ftueStep);
  const serverSynced = useGameStore(g => g.serverSynced);
  const setFtueLocal = useGameStore(g => g.setFtueLocal);

  const advance = useCallback(async (nextStep: number) => {
    setFtueLocal('in_progress', nextStep);
    try { await setFtueState('advance', nextStep); } catch { /* offline */ }
  }, [setFtueLocal]);

  const skipFtue = useCallback(async () => {
    setFtueLocal('skipped');
    try { await setFtueState('skip'); } catch { /* offline */ }
  }, [setFtueLocal]);

  const goToTutorialBattle = useCallback(async () => {
    // ФТУЕ.3 переход: «Да» в TutorialChoice → step=10 (туториал-бой)
    // → редирект на /tutorial-battle. Сам бой реализуется в ФТУЕ.3.
    setFtueLocal('in_progress', 10);
    try { await setFtueState('advance', 10); } catch { /* offline */ }
    navigate('/tutorial-battle');
  }, [setFtueLocal, navigate]);

  // Early returns ПОСЛЕ хуков. Не показываем пока с сервером не
  // синканулись (иначе мелькнёт у завершившего игрока на новом девайсе).
  if (!serverSynced) return null;
  if (ftueStatus !== 'pending' && ftueStatus !== 'in_progress') return null;
  // Шаги 10+ — туториал-бой, обрабатывается в BattleScreen, не здесь.
  if (ftueStep >= 10) return null;

  return (
    <div className={s.overlay}>
      <div className={s.scanlines} />
      {ftueStep === 0 && <SplashStep onContinue={() => advance(1)} />}
      {ftueStep === 1 && <LanguageStep onNext={() => advance(2)} />}
      {ftueStep === 2 && <NickStep onNext={() => advance(3)} />}
      {ftueStep === 3 && <SignalStep onNext={() => advance(4)} />}
      {ftueStep === 4 && <TutorialChoiceStep
        onYes={goToTutorialBattle}
        onNo={skipFtue}
      />}
    </div>
  );
}

// ============================================================
// Step 0 · Splash
// ============================================================
function SplashStep({ onContinue }: { onContinue: () => void }) {
  const { hapticTap } = useTelegram();
  const t = useTr();
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDone(true), 4800);
    return () => clearTimeout(t);
  }, []);

  // Авто-продвинуть через 6 секунд если игрок не нажал.
  useEffect(() => {
    const t = setTimeout(onContinue, 6000);
    return () => clearTimeout(t);
  }, [onContinue]);

  const handleTap = () => {
    if (!done) return; // защита от случайного скипа в самом начале
    hapticTap();
    onContinue();
  };

  return (
    <div className={s.splash} onClick={handleTap}>
      <div className={s.splashMarks}>
        <div className={`${s.splashMark} ${s.m1} ${s.deus}`}>
          <img src="/img/factions/deus.jpg" alt="Deus" />
        </div>
        <div className={`${s.splashMark} ${s.m2} ${s.syn}`}>
          <img src="/img/factions/synt.jpg" alt="Syntetics" />
        </div>
        <div className={`${s.splashMark} ${s.m3} ${s.gsec}`}>
          <img src="/img/factions/g_sec.jpg" alt="Global" />
        </div>
        <div className={`${s.splashMark} ${s.m4} ${s.liv}`}>
          <img src="/img/factions/livlife.jpg" alt="Liv Life" />
        </div>
      </div>
      <h1 className={s.splashTitle}>
        КОРПЫ<span className={s.slash}>/</span>CORPS
      </h1>
      <div className={s.splashTag}>
        <span className={s.typer}>
          {t('Четыре корпы. Одна ставка.', 'Four corps. One bet.')}
        </span>
      </div>
      <div className={s.splashSkip}>{t('Тапни чтобы пропустить', 'Tap to skip')}</div>
    </div>
  );
}

// ============================================================
// Step 1 · Выбор языка
// ============================================================
function LanguageStep({ onNext }: { onNext: () => void }) {
  const { hapticTap, hapticSuccess } = useTelegram();
  const { locale, setLocale } = useLocale();

  const pick = (next: 'ru' | 'en') => {
    hapticTap();
    setLocale(next);
  };

  const onAccept = () => {
    hapticSuccess();
    onNext();
  };

  return (
    <div className={s.screen}>
      <div className={s.kicker}>init · 01/04</div>
      <h1 className={s.title}>Выбери язык</h1>
      <p className={s.subtitle}>Choose your language</p>

      <div className={s.langGrid}>
        <div
          className={`${s.langCard} ${locale === 'ru' ? s.active : ''}`}
          onClick={() => pick('ru')}
        >
          <svg className={s.langFlag} viewBox="0 0 9 6" preserveAspectRatio="none">
            <rect width="9" height="2" fill="#fff" />
            <rect y="2" width="9" height="2" fill="#0039A6" />
            <rect y="4" width="9" height="2" fill="#D52B1E" />
          </svg>
          <div className={s.langName}>
            <div className="nm">Русский</div>
            <div className="en">RU · Russian</div>
          </div>
          <div className={s.langCheck} />
        </div>
        <div
          className={`${s.langCard} ${locale === 'en' ? s.active : ''}`}
          onClick={() => pick('en')}
        >
          <svg className={s.langFlag} viewBox="0 0 60 30">
            <rect width="60" height="30" fill="#012169" />
            <path d="M0,0 60,30 M60,0 0,30" stroke="#fff" strokeWidth="6" />
            <path d="M0,0 60,30 M60,0 0,30" stroke="#C8102E" strokeWidth="3" />
            <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
            <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
          </svg>
          <div className={s.langName}>
            <div className="nm">English</div>
            <div className="en">EN · English</div>
          </div>
          <div className={s.langCheck} />
        </div>
      </div>

      <div className={s.hint}>
        Поменять можно в Настройках → Язык
        <br />
        Change later in Settings → Language
      </div>

      <div className={s.foot}>
        <button className={s.btnPrimary} onClick={onAccept}>
          Принять · Accept
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Step 2 · Позывной
// ============================================================
const NICK_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

function NickStep({ onNext }: { onNext: () => void }) {
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const t = useTr();
  const currentNick = useGameStore(g => g.player.nick);
  const setPlayerName = useGameStore(g => g.setPlayerName);

  const [val, setVal] = useState(currentNick || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Подсосать имя из Telegram (на случай если nick — placeholder типа "user_xxx").
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = (window as any).Telegram?.WebApp?.initDataUnsafe?.user;
    if (tg?.username && (!val || val.startsWith('user_'))) {
      setVal(tg.username);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tgUser = (window as any).Telegram?.WebApp?.initDataUnsafe?.user as
    | { first_name?: string; username?: string }
    | undefined;
  const tgAvatarLetter = (tgUser?.first_name?.[0] || 'C').toUpperCase();
  const tgDisplayName = tgUser?.first_name || t('Чумба', 'Choom');
  const tgAt = tgUser?.username ? `@${tgUser.username}` : '@telegram_user';

  const ok = NICK_REGEX.test(val);
  const count = val.length;
  // Чёткая диагностика: что именно не так с введённым ником.
  const hasInvalidChars = val.length > 0 && /[^a-zA-Z0-9_]/.test(val);
  const tooShort = val.length > 0 && val.length < 3 && !hasInvalidChars;

  const submit = async () => {
    if (!ok || busy) return;
    setBusy(true);
    setErr(null);
    hapticTap();
    try {
      await updateProfile({ nick: val });
      setPlayerName(val);
      hapticSuccess();
      onNext();
    } catch (e: unknown) {
      const msg = String((e as { message?: string })?.message || '');
      hapticError();
      if (msg.includes('nick_taken')) setErr(t('Этот юзернейм уже занят', 'This handle is taken'));
      else setErr(t('Не удалось сохранить. Попробуй ещё.', 'Could not save. Try again.'));
      setBusy(false);
    }
  };

  return (
    <div className={s.screen}>
      <div className={s.kicker}>init · 02/04</div>
      <h1 className={s.title}>{t('Как тебя зовут,', "What's your handle,")}<br />{t('чумба?', 'choom?')}</h1>
      <p className={s.subtitle}>{t('Позывной видят противники в матче', 'Opponents see this handle in matches')}</p>

      <div className={s.nickTg}>
        <div className={s.nickTgAvatar}>{tgAvatarLetter}</div>
        <div className={s.nickTgInfo}>
          <div className="nm">{tgDisplayName}</div>
          <div className="at">{tgAt}</div>
        </div>
        <div className={s.nickTgBadge}>{t('из TG', 'from TG')}</div>
      </div>

      <label className={s.fieldLabel}>{t('Позывной', 'Handle')}</label>
      <div className={s.nickInputWrap}>
        <input
          className={s.nickInput}
          value={val}
          onChange={(e) => { setVal(e.target.value); setErr(null); }}
          maxLength={20}
          autoComplete="off"
          spellCheck={false}
          placeholder={t('например, cyberhunter_99', 'e.g. cyberhunter_99')}
        />
        {val.length > 0 && (
          <span className={s.nickClear} onClick={() => setVal('')}>×</span>
        )}
      </div>
      <div className={s.nickMeta}>
        {err
          ? <span className="bad">{err}</span>
          : hasInvalidChars
            ? <span className="bad">{t('Только латиница (a–z, A–Z), цифры и _', 'Latin only (a–z, A–Z), digits and _')}</span>
            : tooShort
              ? <span className="bad">{t('Минимум 3 символа', 'Min 3 characters')}</span>
              : ok
                ? <span className="ok">{t('формат ок', 'format ok')}</span>
                : <span className="count">{t('только английские буквы, цифры или _ · 3–20', 'latin letters, digits or _ · 3–20')}</span>
        }
        <span className="count">{count} / 20</span>
      </div>

      <div className={s.foot}>
        <button
          className={s.btnPrimary}
          onClick={submit}
          disabled={!ok || busy}
        >
          {busy ? t('Проверка...', 'Checking...') : t('На улицы →', 'Hit the streets →')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Step 3 · Сигнал (реф-код)
// ============================================================
function SignalStep({ onNext }: { onNext: () => void }) {
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const t = useTr();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // localStorage флаг, который ставил main.tsx — снимаем при первом
  // показе SignalStep, чтобы поверх не показалась старая модалка.
  useEffect(() => {
    try { localStorage.removeItem('corps.needsReferrerOnboarding'); } catch { /* */ }
  }, []);

  const onSubmit = async () => {
    const cleaned = code.trim().toUpperCase();
    if (cleaned.length < 2) {
      hapticError();
      setErr(t('Введи Сигнал или пропусти', 'Enter a Beacon or skip'));
      return;
    }
    hapticTap();
    setBusy(true);
    setErr(null);
    try {
      await setReferrer(cleaned);
      hapticSuccess();
      onNext();
    } catch (e: unknown) {
      hapticError();
      const msg = String((e as { message?: string })?.message || '');
      if (msg.includes('code_not_found')) setErr(t('Такого Сигнала нет', 'No such Beacon'));
      else if (msg.includes('self_referral')) setErr(t('Это твой Сигнал, чумба', "That's your own Beacon, choom"));
      else if (msg.includes('cycle_detected')) setErr(t('Этого чумбу ты уже пригласил', 'You already invited this choom'));
      else if (msg.includes('already_has_referrer')) {
        onNext();
        return;
      }
      else setErr(msg || t('Не удалось', 'Failed'));
      setBusy(false);
    }
  };

  const onSkip = () => {
    hapticTap();
    onNext();
  };

  return (
    <div className={s.screen}>
      <div className={s.kicker}>init · 03/04</div>
      <h1 className={s.title}>{t('Входящий', 'Incoming')} <em>{t('Сигнал', 'Beacon')}</em></h1>
      <p className={s.subtitle}>
        {t(
          'Чумба, тебя позвал в Корпы кто-то из своих? Если да — введи его Сигнал. После обучения ты получишь Общий ШК и 20 Скриптов, а пригласивший — Продвинутый ШК.',
          "Choom, did anyone bring you to Corps? If yes — enter their Beacon. After the tutorial you get a Standard CC and 20 Scripts, and your inviter gets an Advanced CC."
        )}
      </p>

      <label className={s.fieldLabel}>{t('Код Сигнала', 'Beacon code')}</label>
      <div className={s.nickInputWrap}>
        <input
          className={s.nickInput}
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null); }}
          placeholder={t('напр. XR4ZQK', 'e.g. XR4ZQK')}
          maxLength={16}
          autoComplete="off"
          spellCheck={false}
          style={{ letterSpacing: '0.20em', fontSize: 18, textAlign: 'center' }}
        />
      </div>
      {err && (
        <div className={s.nickMeta} style={{ marginTop: 4 }}>
          <span className="bad">{err}</span>
        </div>
      )}

      <div className={s.foot}>
        <button
          className={s.btnPrimary}
          onClick={onSubmit}
          disabled={busy || code.trim().length < 2}
        >
          {busy ? t('Проверка...', 'Checking...') : t('Принять Сигнал', 'Accept Beacon')}
        </button>
        <button className={s.btnGhost} onClick={onSkip} disabled={busy}>
          {t('Никто меня не звал', 'No one invited me')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Step 4 · Пройти обучение?
// ============================================================
function TutorialChoiceStep({ onYes, onNo }: { onYes: () => void; onNo: () => void }) {
  const { hapticTap, hapticSuccess } = useTelegram();
  const t = useTr();

  const yes = () => {
    hapticSuccess();
    onYes();
  };
  const no = () => {
    hapticTap();
    onNo();
  };

  return (
    <div className={s.screen}>
      <div className={s.kicker}>init · 04/04</div>
      <h1 className={s.title}>{t('Пройти', 'Take the')} <em>{t('обучение?', 'tutorial?')}</em></h1>
      <p className={s.subtitle}>
        {t(
          'Один туториал-бой, потом подсказки по интерфейсу. Прерывается в любой момент.',
          'One tutorial battle, then UI hints. Can quit anytime.'
        )}
      </p>

      <div className={s.choices}>
        <div className={`${s.choice} ${s.yes}`} onClick={yes}>
          <div className={s.choiceHead}>
            <span className={s.choiceLabel}>{t('Да', 'Yes')}</span>
            <span className={s.choiceTag}>{t('+ бонус', '+ bonus')}</span>
          </div>
          <p className={s.choiceLead}>
            {t('Базовая база + награды для старта. Рекомендуется.', 'Starter pack + extra rewards. Recommended.')}
          </p>
          <div className={s.bonusList}>
            <div className={s.bonus}>
              <div className={s.bonusIco}>{t('ШК', 'CC')}</div>
              <div className={s.bonusTxt}>×1 <b>{t('Продвинутый', 'Advanced')}</b> {t('шифроконтейнер', 'Cipher Crate')}</div>
            </div>
            <div className={s.bonus}>
              <div className={s.bonusIco}>{t('ШК', 'CC')}</div>
              <div className={s.bonusTxt}>×1 <b>{t('Основной', 'Standard')}</b> {t('шифроконтейнер', 'Cipher Crate')}</div>
            </div>
            <div className={`${s.bonus} ${s.title}`}>
              <div className={s.bonusIco}>★</div>
              <div className={s.bonusTxt}>
                {t('Титул', 'Title')} <b style={{ color: 'var(--neon-amber, #FFB13D)' }}>{t('«Из учебки»', '«Fresh Out the Boot»')}</b> — {t('навсегда в профиле', 'forever in your profile')}
              </div>
            </div>
          </div>
          <div className={s.bonusUsd}>≈ $8.50 {t('+ эксклюзивный титул', '+ exclusive title')}</div>
        </div>

        <div className={`${s.choice} ${s.no}`} onClick={no}>
          <div className={s.choiceHead}>
            <span className={s.choiceLabel}>{t('Нет', 'No')}</span>
            <span className={s.choiceTag}>{t('база всё равно', 'starter pack anyway')}</span>
          </div>
          <p className={s.choiceLead}>
            {t(
              'Пропустить, но мы всё равно дадим минимальную колоду и базовые ШК. Туториал доступен из настроек.',
              "Skip — but we'll still give you a starter deck and base Cipher Crates. Tutorial is available from Settings."
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
